import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Users,
  FileText,
  Calendar,
  MapPin,
  Mail,
  Phone,
  AlertTriangle,
  CheckCircle,
  X,
  Key,
  RefreshCw,
  Copy,
  CreditCard,
  UserPlus,
  Shield,
  Settings,
  Power,
  PowerOff
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import DatabaseService from '../../services/databaseService';
import AdminAssignmentModal from './AdminAssignmentModal';
// import EmailService from '../../services/emailService';

const ClinicManagement = ({ onUpdate }) => {
  const [clinics, setClinics] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'details'
  const [loading, setLoading] = useState(true);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isManualPassword, setIsManualPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [isMounted, setIsMounted] = useState(true);
  const [error, setError] = useState(null);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedClinicForAdmin, setSelectedClinicForAdmin] = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Delete clinic function
  const deleteClinic = async (clinic, index) => {
    console.log('DELETE CLINIC FUNCTION CALLED');
    console.log('Clinic data:', clinic);
    console.log('Index:', index);
    console.log('Current clinics count:', clinics.length);

    try {
      const clinicName = clinic?.name || `Clinic ${index + 1}`;
      console.log('Clinic name to delete:', clinicName);

      if (!clinic?.id) {
        console.error('ERROR: No clinic ID provided');
        toast.error('Cannot delete: No clinic ID found');
        return;
      }

      console.log('Clinic ID to delete:', clinic.id);

      // Show confirmation dialog
      const confirmDelete = window.confirm(`Delete "${clinicName}"?\n\nThis action cannot be undone.`);
      console.log('User confirmed delete:', confirmDelete);

      if (!confirmDelete) {
        console.log('User cancelled delete');
        return;
      }

      console.log('Proceeding with delete operation...');
      console.log('Deleting clinic ID:', clinic.id);

      // Show loading toast
      const loadingToast = toast.loading('Deleting clinic...');

      try {
        // Use DatabaseService.delete to delete the clinic directly
        const deleteResult = await DatabaseService.delete('clinics', clinic.id);
        console.log('DatabaseService.delete result:', deleteResult);

        // Dismiss loading toast
        toast.dismiss(loadingToast);

        toast.success(`${clinicName} deleted successfully`);
        console.log('Delete operation completed successfully');

        // Reload clinics to refresh the list
        console.log('Reloading clinics list...');
        await loadClinics();
        console.log('Clinics reloaded. New count:', clinics.length);

        // Trigger refresh to ensure UI is in sync
        if (onUpdate) {
          console.log('Calling onUpdate callback...');
          onUpdate();
        }
      } catch (deleteError) {
        // Dismiss loading toast
        toast.dismiss(loadingToast);
        throw deleteError;
      }

    } catch (error) {
      console.error('Delete error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Clinic data:', clinic);
      console.error('Index:', index);

      // Show detailed error message
      toast.error(`Failed to delete clinic: ${error.message}`);
    }
  };

  useEffect(() => {
    // Load clinics properly on component mount
    const initializeClinics = async () => {
      try {
        console.log('START: Initializing clinic management...');
        setLoading(true);
        
        // Load clinics with proper error handling
        await loadClinics(false); // Don't skip cleanup to ensure proper data loading
        
        console.log('SUCCESS: Clinic initialization complete');
        
      } catch (error) {
        console.error('ERROR: Error initializing clinics:', error);
        setClinics([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Add a small delay to ensure database is ready
    const timer = setTimeout(() => {
      initializeClinics();
    }, 100);
    
    // Configure email service with error handling
    try {
      // EmailService.setProvider('emailjs', { publicKey: 'your_key_here' });
      // EmailService.setProvider('sendgrid', { apiKey: 'your_key_here' });
      console.log('Email service temporarily disabled for debugging');
    } catch (error) {
      console.warn('Email service configuration failed:', error);
    }
    
    // Cleanup function
    return () => {
      clearTimeout(timer);
      setIsMounted(false);
    };
  }, []);

  const cleanupDemoData = async () => {
    try {
      console.log('CLEANUP: Checking for demo clinic data...');
      
      // Get current clinic data safely
      let clinicsData = [];
      try {
        clinicsData = await DatabaseService.get('clinics') || [];
      } catch (error) {
        console.warn('Could not get clinic data for cleanup:', error);
        return; // Exit safely if we can't get data
      }
      
      console.log('INFO: Total clinics before cleanup:', clinicsData.length);
      
      // Skip cleanup if no data
      if (!Array.isArray(clinicsData) || clinicsData.length === 0) {
        console.log('No clinic data to clean');
        return;
      }
      
      // Identify demo/test clinics
      const demoClinicIndicators = [
        'demo',
        'test',
        'sample',
        'clinic@demo.com',
        'demo@',
        '@demo.com',
        'example.com'
      ];
      
      const realClinics = clinicsData.filter(clinic => {
        // Ensure clinic has required properties
        if (!clinic || typeof clinic !== 'object') {
          return true; // Keep non-object entries (shouldn't happen, but be safe)
        }
        
        const name = String(clinic.name || '').toLowerCase();
        const email = String(clinic.email || '').toLowerCase();
        
        // Check if clinic name or email contains demo indicators
        const isDemo = demoClinicIndicators.some(indicator => 
          name.includes(indicator) || email.includes(indicator)
        );
        
        if (isDemo) {
          console.log(`DELETE: Removing demo clinic: ${clinic.name} - ${clinic.email}`);
          return false;
        }
        
        return true;
      });
      
      // If we found demo clinics to remove, update the database
      if (realClinics.length < clinicsData.length) {
        console.log(`SUCCESS: Removing ${clinicsData.length - realClinics.length} demo clinics`);
        
        try {
          // Only update if component is still mounted
          if (!isMounted) return;
          
          // Clear and repopulate with real clinics only
          localStorage.setItem('clinics', JSON.stringify(realClinics));
          
          // Also update database if available
          if (DatabaseService.usedatabase) {
            // Remove demo clinics from database
            for (const clinic of clinicsData) {
              if (!clinic || !clinic.id) continue; // Skip invalid entries
              
              const name = String(clinic.name || '').toLowerCase();
              const email = String(clinic.email || '').toLowerCase();
              
              const isDemo = demoClinicIndicators.some(indicator => 
                name.includes(indicator) || email.includes(indicator)
              );
              
              if (isDemo) {
                try {
                  await DatabaseService.delete('clinics', clinic.id);
                } catch (error) {
                  console.warn(`Failed to delete demo clinic ${clinic.id} from database:`, error);
                }
              }
            }
          }
          
          console.log(`COMPLETE: Cleanup complete! ${realClinics.length} real clinics remaining`);
        } catch (storageError) {
          console.error('Failed to update storage after cleanup:', storageError);
          throw storageError; // Re-throw to let caller handle
        }
      } else {
        console.log('SUCCESS: No demo clinics found - data is clean');
      }
      
    } catch (error) {
      console.error('ERROR: Demo data cleanup failed:', error);
      throw error; // Re-throw to let caller handle
    }
  };

  const migrateLocalStorageTodatabase = async () => {
    try {
      // Check if database is available
      if (!DatabaseService.usedatabase) {
        console.log('INFO: database not available, skipping migration');
        return;
      }

      // Check if database already has data
      const existingClinics = await DatabaseService.get('clinics');
      if (existingClinics.length > 0) {
        console.log('DATA: database already has data, skipping migration');
        return;
      }

      // Get localStorage data
      const localStorageData = JSON.parse(localStorage.getItem('clinics') || '[]');
      if (localStorageData.length === 0) {
        console.log('STORAGE: No localStorage data to migrate');
        return;
      }

      console.log('START: Starting migration of', localStorageData.length, 'clinics to database...');
      
      // Migrate each clinic
      for (const clinic of localStorageData) {
        try {
          await DatabaseService.add('clinics', clinic);
          console.log('SUCCESS: Migrated clinic:', clinic.name);
        } catch (error) {
          console.error('ERROR: Failed to migrate clinic:', clinic.name, error);
        }
      }
      
      console.log('COMPLETE: Migration completed successfully!');
      
    } catch (error) {
      console.error('ERROR: Migration failed:', error);
    }
  };

  const loadClinics = async (skipCleanup = false) => {
    try {
      console.log('SUPERADMIN: SuperAdmin loading all registered clinics...');
      console.log('TIME: Load time:', new Date().toLocaleTimeString());
      
      // Don't clear state immediately - wait until we have new data
      // setClinics([]); // Removed this line to prevent flashing empty state
      
      // Try to migrate localStorage data to database if needed
      await migrateLocalStorageTodatabase();
      
      // Clean demo data after migration (with error protection) - only if explicitly requested
      if (!skipCleanup) {
        try {
          await cleanupDemoData();
        } catch (cleanupError) {
          console.warn('Demo cleanup failed, continuing with normal load:', cleanupError);
        }
      }
      
      // SuperAdmin can see ALL clinics - fetch from database or localStorage
      let clinicsData = [];
      
      try {
        clinicsData = await DatabaseService.get('clinics');
        console.log('DATA: Data from DatabaseService:', clinicsData.length, 'clinics');
        
        // Additional debug logging
        if (clinicsData && clinicsData.length > 0) {
          console.log('DEBUG: First clinic details:', {
            name: clinicsData[0].name,
            email: clinicsData[0].email,
            id: clinicsData[0].id,
            isActive: clinicsData[0].isActive
          });
          
          // Log all clinics for debugging
          clinicsData.forEach((clinic, index) => {
            console.log(`DEBUG: Clinic ${index + 1}:`, {
              name: clinic.name,
              email: clinic.email,
              id: clinic.id,
              isActive: clinic.isActive,
              isActivated: clinic.isActivated,
              subscriptionStatus: clinic.subscriptionStatus,
              subscription_status: clinic.subscription_status,
              is_active: clinic.is_active,
              reports_allowed: clinic.reports_allowed
            });
          });
        } else {
          console.log('EMPTY: No clinics found in database');
        }
      } catch (error) {
        console.error('ERROR: Error getting clinics from DatabaseService:', error);
        clinicsData = [];
      }
      
      // Debug: Log raw data
      console.log('DEBUG: Raw clinics data from DB:', clinicsData);
      console.log('DEBUG: Raw localStorage data:', JSON.parse(localStorage.getItem('clinics') || '[]'));
      
      // Ensure clinicsData is an array and contains valid objects
      if (!Array.isArray(clinicsData)) {
        console.warn('WARNING: Clinics data is not an array:', clinicsData);
        clinicsData = [];
      }
      
      // Filter out invalid clinic objects
      clinicsData = clinicsData.filter(clinic => 
        clinic && 
        typeof clinic === 'object' && 
        (clinic.name || clinic.email || clinic.id)
      );
      
      console.log('CLINIC: Found valid clinics:', clinicsData.length);
      console.log('INFO: Clinic names:', clinicsData.map(c => c && c.name || 'Unknown'));
      
      // Sort by creation date (newest first)
      const sortedClinics = clinicsData.sort((a, b) => 
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      
      // Only update state if component is still mounted
      if (isMounted) {
        setClinics(sortedClinics);
      }
      console.log('SUCCESS: Clinics loaded and sorted:', sortedClinics.length);
      
      // Debug: Check for duplicate IDs or missing IDs
      const clinicIds = sortedClinics.map(c => c && c.id || 'unknown-id');
      const uniqueIds = [...new Set(clinicIds)];
      if (clinicIds.length !== uniqueIds.length) {
        console.warn('WARNING: DUPLICATE IDs found!');
        console.log('All IDs:', clinicIds);
        console.log('Unique IDs:', uniqueIds);
      }
      
      // Debug: Check for missing IDs
      const missingIds = sortedClinics.filter(c => !c.id);
      if (missingIds.length > 0) {
        console.warn('WARNING: Clinics without ID found:', missingIds.length);
        console.log('Clinics without ID:', missingIds);
        
        // Auto-generate IDs for clinics that don't have them
        console.log('REFRESH: Auto-generating missing IDs...');
        missingIds.forEach((clinic, index) => {
          if (!clinic.id) {
            clinic.id = `clinic-${Date.now()}-${index}`;
            console.log(`SUCCESS: Generated ID for clinic "${clinic.name || 'Unknown'}": ${clinic.id}`);
          }
        });
        
        // Save the updated clinics with IDs
        try {
          localStorage.setItem('clinics', JSON.stringify(sortedClinics));
          console.log('STORAGE: Updated clinics saved with generated IDs');
        } catch (error) {
          console.error('ERROR: Failed to save clinics with generated IDs:', error);
        }
      }
      
      // Debug: List all clinic details
      console.table(sortedClinics.map(c => ({
        name: c && c.name || 'Unknown',
        id: c && c.id || 'No ID',
        isActive: c && c.isActive || false,
        isActivated: c && c.isActivated || false,
        email: c && c.email || 'No email'
      })));
    } catch (error) {
      console.error('ERROR: Error loading clinics:', error);
      if (isMounted) {
        toast.error('Error loading clinics: ' + error.message);
        setError('Failed to load clinics: ' + error.message);
        // Set empty array to prevent further errors
        setClinics([]);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const handleCreateClinic = async (data) => {
    try {
      // Set reports limit based on subscription plan if not manually set
      let reportsAllowed = data.reportsAllowed || 10;
      if (data.subscriptionPlan && !data.reportsAllowed) {
        switch (data.subscriptionPlan) {
          case 'trial': reportsAllowed = 10; break;
          case 'basic': reportsAllowed = 50; break;
          case 'premium': reportsAllowed = 200; break;
          case 'enterprise': reportsAllowed = -1; break; // Unlimited
          default: reportsAllowed = 10;
        }
      }

      const clinicData = {
        ...data,
        contactPerson: data.contactPerson || data.name,
        subscription_status: data.subscriptionPlan || 'trial', // Use snake_case for consistency
        subscriptionStatus: data.subscriptionPlan || 'trial', // Legacy field
        reports_allowed: reportsAllowed,
        reportsAllowed: reportsAllowed, // Legacy field
        reports_used: 0,
        reportsUsed: 0, // Legacy field
        is_active: true, // Super admin created clinics are pre-approved
        isActive: true, // Legacy field
        isActivated: true, // Super admin created clinics are pre-approved
        trial_start_date: new Date().toISOString(),
        trial_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days trial
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date().toISOString(), // Legacy field
        registrationMethod: 'super_admin_created', // Track how this was created
        // Use single password field for authentication
        password: data.password, // This is the field used for login
        // Remove confirmPassword from stored data
        confirmPassword: undefined
      };
      
      const createdClinic = await DatabaseService.add('clinics', clinicData);

      toast.success(`Project Area "${data.name}" created and activated successfully with ${data.subscriptionPlan || 'trial'} plan! They can now login.`);
      loadClinics();
      setShowModal(false);
      reset();
      onUpdate?.();

    } catch (error) {
      toast.error('Error creating project area: ' + (error.message || 'Unknown error'));
      console.error('Error creating project area:', error);
    }
  };

  const handleEditClinic = async (data) => {
    try {
      if (!selectedClinic?.id) {
        toast.error('No clinic selected for editing');
        return;
      }
      await DatabaseService.update('clinics', selectedClinic.id, data);
      toast.success('Project Area updated successfully');
      loadClinics();
      setShowModal(false);
      setSelectedClinic(null);
      reset();
      onUpdate?.();
    } catch (error) {
      toast.error('Error updating project area: ' + (error.message || 'Unknown error'));
      console.error('Error updating project area:', error);
    }
  };

  const handleDeactivateClinic = async (clinicId) => {
    try {
      const clinic = await DatabaseService.findById('clinics', clinicId);
      const newStatus = !clinic.isActive;
      const action = newStatus ? 'activate' : 'deactivate';

      if (window.confirm(`Are you sure you want to ${action} "${clinic.name}"?\n\n${newStatus ? 'Project Area will be able to login and use the system.' : 'Project Area will be unable to login or access the system.'}`)) {

        // Prepare update data
        const updateData = {
          isActive: newStatus,
          is_active: newStatus, // Also update snake_case field for consistency
          updated_at: new Date().toISOString()
        };

        // If activating the clinic, also update subscription status if it's pending_approval
        if (newStatus && (clinic.subscription_status === 'pending_approval' || clinic.subscriptionStatus === 'pending_approval')) {
          updateData.subscription_status = 'trial'; // Change from pending_approval to trial
          updateData.subscriptionStatus = 'trial'; // Legacy field
          updateData.reports_allowed = 10; // Give trial credits
          updateData.reportsAllowed = 10; // Legacy field
          updateData.reports_used = 0; // Reset usage
          updateData.reportsUsed = 0; // Legacy field
          updateData.trial_start_date = new Date().toISOString();
          updateData.trial_end_date = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days trial
          updateData.activatedAt = new Date().toISOString();
        }

        await DatabaseService.update('clinics', clinicId, updateData);

        const successMessage = newStatus
          ? `Project Area "${clinic.name}" activated successfully! They can now login with their credentials.`
          : `Project Area "${clinic.name}" deactivated successfully.`;

        toast.success(successMessage, {
          duration: 4000
        });
        loadClinics();
        onUpdate?.();
      }
    } catch (error) {
      toast.error('Error updating project area status: ' + (error.message || 'Unknown error'));
      console.error('Error in handleDeactivateClinic:', error);
    }
  };

  const handleDeleteClinic = async (clinicId) => {
    console.log('DELETE: DELETE FUNCTION CALLED for clinic ID:', clinicId);
    console.log('DATA: Current time:', new Date().toISOString());
    console.log('INFO: All clinics before delete:', clinics.map(c => ({ id: c.id, name: c.name })));
    
    // Force an alert to ensure the function is being called
    alert(`Delete function called for clinic ID: ${clinicId}`);
    
    if (!clinicId) {
      console.error('ERROR: No clinic ID provided for deletion');
      alert('ERROR: No clinic ID found');
      toast.error('Cannot delete: No clinic ID found');
      return;
    }
    
    console.log('DEBUG: Showing confirmation dialog...');
    const confirmed = window.confirm(`Are you sure you want to delete clinic with ID: ${clinicId}?\n\nThis action cannot be undone.`);
    console.log('SUCCESS: User confirmation result:', confirmed);
    
    if (confirmed) {
      try {
        console.log('SUCCESS: User confirmed deletion, proceeding...');
        console.log('REFRESH: Calling DatabaseService.delete with:', { table: 'clinics', id: clinicId });
        
        // First, let's manually check localStorage before deletion
        const currentData = JSON.parse(localStorage.getItem('clinics') || '[]');
        console.log(' Current localStorage data before delete:', currentData);
        
        const result = await DatabaseService.delete('clinics', clinicId);
        console.log('SUCCESS: DatabaseService.delete result:', result);
        
        // Check localStorage after deletion
        const afterData = JSON.parse(localStorage.getItem('clinics') || '[]');
        console.log(' localStorage data after delete:', afterData);
        
        toast.success('Clinic deleted successfully!');
        console.log('REFRESH: Reloading clinics list...');
        await loadClinics();
        onUpdate?.();
        console.log('SUCCESS: Delete operation completed successfully');
      } catch (error) {
        console.error('ERROR: Error during delete operation:', error);
        console.error('Error details:', { 
          message: error.message, 
          stack: error.stack,
          clinicId: clinicId 
        });
        alert(`Delete failed: ${error.message}`);
        toast.error('Error deleting clinic: ' + error.message);
      }
    } else {
      console.log('ERROR: User cancelled deletion');
      alert('Delete cancelled by user');
    }
  };

  const handleManualActivation = async (clinicId) => {
    if (window.confirm('Manually activate this clinic? This will allow them to login to their clinic portal.')) {
      try {
        // Get clinic details for notification
        const clinic = await DatabaseService.findById('clinics', clinicId);

        // Update local database
        await DatabaseService.update('clinics', clinicId, {
          isActivated: true,
          activatedAt: new Date().toISOString(),
          activationOTP: null,
          otpExpiresAt: null
        });

        // If clinic has Supabase credentials, update their email confirmation status
        if (clinic.supabaseUserId) {
          try {
            console.log('EMAIL: Updating Supabase profile for activated clinic...');
            // Note: In production, you'd need admin Supabase credentials to update user records
            // For now, we'll rely on the local database authentication fallback
          } catch (supabaseError) {
            console.warn('WARNING: Could not update Supabase profile:', supabaseError);
          }
        }

        toast.success(`Project Area "${clinic.name}" activated successfully! They can now login to their portal.`);
        loadClinics();
        onUpdate?.();

        // Show activation confirmation
        setTimeout(() => {
          toast.success(`EMAIL: Activation notification would be sent to ${clinic.email}`, {
            duration: 3000
          });
        }, 1000);

      } catch (error) {
        toast.error('Error activating project area: ' + (error.message || 'Unknown error'));
        console.error('Error activating project area:', error);
      }
    }
  };

  const handleClinicApproval = async (clinicId) => {
    if (window.confirm('Approve this clinic registration? This will activate their account and give them trial credits.')) {
      try {
        // Get clinic details
        const clinic = await DatabaseService.findById('clinics', clinicId);

        // Update clinic status to approved and activated
        await DatabaseService.update('clinics', clinicId, {
          subscription_status: 'trial', // Change from 'pending_approval' to 'trial'
          is_active: true, // Activate the clinic
          isActivated: true, // Legacy field for compatibility
          reports_allowed: 10, // Give trial credits
          trial_start_date: new Date().toISOString(),
          trial_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days trial
          activatedAt: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

        toast.success(`Project Area "${clinic.name}" approved and activated! They now have 10 trial reports and can login.`, {
          duration: 4000
        });

        // Reload clinic list to reflect changes
        loadClinics();
        onUpdate?.();

      } catch (error) {
        toast.error('Error approving project area registration: ' + (error.message || 'Unknown error'));
        console.error('Project area approval error:', error);
      }
    }
  };

  const handleAutoClinicLogin = async (clinic) => {
    try {
      console.log('START: Auto-login attempt for clinic:', clinic.name);

      // Create a mock login session for the clinic
      const clinicUser = {
        id: clinic.id,
        email: clinic.email,
        name: clinic.name,
        role: 'clinic_admin',
        avatar: clinic.logo_url || null,
        isActivated: true,
        clinicId: clinic.id,
        token: `auto_login_${Date.now()}`
      };

      // Save to localStorage for session persistence
      localStorage.setItem('aims_user', JSON.stringify(clinicUser));
      localStorage.setItem('aims_token', clinicUser.token);

      // Show success message
      toast.success(`COMPLETE: Auto-login successful! Redirecting to ${clinic.name} clinic dashboard...`, {
        duration: 2000
      });

      // Redirect to clinic dashboard after short delay
      setTimeout(() => {
        // Redirect to clinic portal (adjust URL as needed)
        window.location.href = '/clinic'; // or '/dashboard' based on your routing
      }, 2000);

    } catch (error) {
      console.error('ERROR: Auto-login failed:', error);
      toast.error('Auto-login failed. Please login manually with clinic credentials.');
    }
  };

  const openModal = (clinic = null) => {
    setSelectedClinic(clinic);
    if (clinic) {
      reset(clinic);
    } else {
      reset({});
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedClinic(null);
    reset({});
  };

  const viewClinicDetails = (clinic) => {
    setSelectedClinic(clinic);
    setViewMode('details');
  };

  const openAdminAssignment = (clinic) => {
    setSelectedClinicForAdmin(clinic);
    setShowAdminModal(true);
  };

  const generateRandomPassword = () => {
    const length = 8;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString(); // 6-digit OTP
  };

  const handlePasswordReset = async (clinic) => {
    setSelectedClinic(clinic);
    setNewPassword('');
    setIsManualPassword(false);
    setOtp('');
    setShowPasswordReset(true);
  };

  const generateNewPassword = () => {
    const password = generateRandomPassword();
    setNewPassword(password);
    setIsManualPassword(false);
  };

  const handleManualPassword = () => {
    setIsManualPassword(true);
    setNewPassword('');
  };

  const sendCredentialsEmail = async (clinic, password, otp) => {
    try {
      console.log(`Sending credentials email to ${clinic.email}...`);
      console.log('Email service temporarily disabled - showing credentials instead');
      
      // Temporarily disabled email service for debugging
      console.log('=== EMAIL DISABLED - MANUAL DELIVERY REQUIRED ===');
      console.log(`Clinic: ${clinic.name}`);
      console.log(`Email: ${clinic.email}`);
      console.log(`Username: ${clinic.email}`);
      console.log(`Password: ${password}`);
      console.log(`OTP: ${otp}`);
      console.log('==============================================');
      
      // Simulate email success for now
      return true;
    } catch (error) {
      console.error('Failed to send email:', error);
      throw error;
    }
  };

  const confirmPasswordReset = async () => {
    if (!newPassword.trim()) {
      toast.error('Please enter a password');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const otpCode = generateOTP();
      
      // Update clinic with new password and OTP
      if (!selectedClinic?.id) {
        toast.error('No clinic selected for password reset');
        return;
      }
      await DatabaseService.update('clinics', selectedClinic.id, {
        password: newPassword,
        passwordResetAt: new Date().toISOString(),
        activationOTP: otpCode,
        isActivated: false,
        otpExpiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString() // 15 minutes
      });

      // Try to send email with credentials and OTP
      try {
        console.log('Sending email with credentials:', {
          clinic: selectedClinic?.name || 'Unknown Clinic',
          email: selectedClinic?.email || 'Unknown Email',
          username: selectedClinic?.email || 'Unknown Email',
          password: newPassword,
          otp: otpCode
        });
        
        await sendCredentialsEmail(selectedClinic, newPassword, otpCode);
        toast.success('SUCCESS: Password set successfully! Credentials and activation OTP sent to clinic email.', {
          duration: 5000
        });
      } catch (emailError) {
        // Email failed, but password was set - show manual delivery option
        toast.error('WARNING: Password set but email failed. Please manually share credentials with clinic.', {
          duration: 8000
        });
        
        // Show credentials in a separate modal or alert for manual delivery
        const credentialsMessage = `
EMAIL DELIVERY FAILED - MANUAL DELIVERY REQUIRED:

Clinic: ${selectedClinic?.name || 'Unknown Clinic'}
Email: ${selectedClinic?.email || 'Unknown Email'}
Username: ${selectedClinic?.email || 'Unknown Email'}
Password: ${newPassword}
OTP: ${otpCode}
Expires: 15 minutes

Please manually share these credentials with the clinic.`;
        
        // Show alert with credentials
        alert(credentialsMessage);
        
        // Also copy to clipboard for easy sharing
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(credentialsMessage);
            toast.success('INFO: Credentials copied to clipboard for manual sharing');
          } else {
            // Fallback for non-secure context
            const textArea = document.createElement('textarea');
            textArea.value = credentialsMessage;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            toast.success('INFO: Credentials copied to clipboard (fallback)');
          }
        } catch (clipboardError) {
          console.warn('Could not copy to clipboard:', clipboardError);
          toast.info('NOTE: Please manually copy the credentials from the alert above');
        }
      }
      
      setShowPasswordReset(false);
      setNewPassword('');
      setSelectedClinic(null);
      setIsManualPassword(false);
      setOtp('');
      onUpdate?.();
    } catch (error) {
      toast.error('Error setting password');
      console.error(error);
    }
  };

  const copyPasswordToClipboard = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(newPassword);
        toast.success('Password copied to clipboard!');
      } else {
        // Fallback for non-secure context
        const textArea = document.createElement('textarea');
        textArea.value = newPassword;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Password copied to clipboard!');
      }
    } catch (error) {
      console.warn('Could not copy to clipboard:', error);
      toast.info('NOTE: Please manually copy the password');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 font-medium">Loading Engineer Management...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto mt-20">
          <h3 className="text-red-800 font-semibold">Error Loading Engineer Management</h3>
          <p className="text-red-600 mt-2 text-sm">{error}</p>
          <div className="flex space-x-3 mt-4">
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                loadClinics(false);
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Retry
            </button>
            <button
              onClick={() => window.location.href = '/admin'}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'details' && selectedClinic) {
    return <ClinicDetails 
      clinic={selectedClinic} 
      onBack={() => {setViewMode('list'); setSelectedClinic(null);}} 
    />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      {/* Clean Professional Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-4">
        <div className="p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="space-y-3 flex-1">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    Engineer Management
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                    Manage your registered project areas and engineers
                  </p>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center space-x-1.5 bg-gray-50 px-2.5 py-1.5 rounded-lg">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <span className="text-xs font-medium text-gray-700">
                    {loading ? (
                      <div className="flex items-center space-x-1.5">
                        <div className="animate-spin rounded-full h-2.5 w-2.5 border-2 border-green-500 border-t-transparent"></div>
                        <span>Loading...</span>
                      </div>
                    ) : (
                      `${clinics.length} Active Clinics`
                    )}
                  </span>
                </div>

                <div className="flex items-center space-x-1.5 bg-gray-50 px-2.5 py-1.5 rounded-lg">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs font-medium text-gray-600">
                    Updated: {new Date().toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-row gap-2 w-full lg:w-auto">
              <button
                onClick={async () => {
                  console.log('REFRESH: Manually refreshing clinic data...');
                  setLoading(true);
                  try {
                    await loadClinics(false);
                    toast.success('Clinic data refreshed successfully!');
                  } catch (error) {
                    console.error('Refresh failed:', error);
                    toast.error('Failed to refresh data');
                  } finally {
                    setLoading(false);
                  }
                }}
                className="flex items-center justify-center space-x-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-1 lg:flex-initial"
                title="Refresh clinic data from database"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>Refresh Data</span>
              </button>

              <button
                onClick={() => openModal()}
                className="flex items-center justify-center space-x-1.5 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-1 lg:flex-initial"
              >
                <Plus className="h-4 w-4" />
                <span>Add Project Area</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* All Project Areas Section */}
      <div className="relative">
        <div className="text-center mb-4">
          <div className="inline-flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="w-6 h-6 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Users className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {loading ? (
                <div className="flex items-center space-x-1.5">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-500 border-t-transparent"></div>
                  <span>Loading Project Areas...</span>
                </div>
              ) : (
                `All Project Areas (${clinics.length})`
              )}
            </h2>
          </div>
        </div>
        
        {/* Modern Table View - Fully Responsive */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Clinic</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden md:table-cell">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden lg:table-cell">Contact</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden xl:table-cell">Phone</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Status</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden sm:table-cell">Verification</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden lg:table-cell">Subscription</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden xl:table-cell">Reports Usage</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {(() => {
            try {
              console.log('DEBUG: RENDERING: Starting to render clinics table');
              console.log('DEBUG: RENDERING: Clinics array:', clinics);
              console.log('DEBUG: RENDERING: Clinics length:', clinics.length);
              console.log('DEBUG: RENDERING: Clinics type:', typeof clinics);
              console.log('DEBUG: RENDERING: Is Array:', Array.isArray(clinics));
              
              if (!Array.isArray(clinics)) {
                console.error('ERROR: CRITICAL: clinics is not an array!', clinics);
                return (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center">
                      <div className="bg-red-100 border border-red-300 rounded-lg p-4 inline-block">
                        <p className="text-red-700">Error: Clinics data is not an array</p>
                        <p className="text-xs text-red-500">Type: {typeof clinics}</p>
                      </div>
                    </td>
                  </tr>
                );
              }
              
              return clinics.map((clinic, index) => {
                try {
                  console.log(`DEBUG: CLINIC ${index}: Starting render for clinic at index ${index}`);
                  console.log(`DEBUG: CLINIC ${index}: Raw clinic data:`, clinic);
                  console.log(`DEBUG: CLINIC ${index}: Clinic type:`, typeof clinic);
                  console.log(`DEBUG: CLINIC ${index}: Clinic constructor:`, clinic ? clinic.constructor.name : 'null');
                  
                  // Safety check for clinic object
                  if (clinic === null) {
                    console.warn(`WARNING: CLINIC ${index}: clinic is null`);
                    return (
                      <div key={`null-${index}`} className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
                        <p className="text-yellow-700">Warning: Null clinic at index {index}</p>
                      </div>
                    );
                  }
                  
                  if (clinic === undefined) {
                    console.warn(`WARNING: CLINIC ${index}: clinic is undefined`);
                    return (
                      <div key={`undefined-${index}`} className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
                        <p className="text-yellow-700">Warning: Undefined clinic at index {index}</p>
                      </div>
                    );
                  }
                  
                  if (typeof clinic !== 'object') {
                    console.warn(`WARNING: CLINIC ${index}: clinic is not an object:`, typeof clinic, clinic);
                    return (
                      <div key={`invalid-${index}`} className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
                        <p className="text-yellow-700">Warning: Invalid clinic object at index {index}</p>
                        <p className="text-xs text-yellow-600">Type: {typeof clinic}</p>
                        <p className="text-xs text-yellow-600">Value: {String(clinic)}</p>
                      </div>
                    );
                  }
                  
                  // Deep inspection of clinic object
                  console.log(`DEBUG: CLINIC ${index}: clinic.name:`, clinic.name);
                  console.log(`DEBUG: CLINIC ${index}: clinic.name type:`, typeof clinic.name);
                  console.log(`DEBUG: CLINIC ${index}: clinic.name === null:`, clinic.name === null);
                  console.log(`DEBUG: CLINIC ${index}: clinic.name === undefined:`, clinic.name === undefined);
                  console.log(`DEBUG: CLINIC ${index}: clinic.name length:`, clinic.name ? clinic.name.length : 'N/A');
                  console.log(`DEBUG: CLINIC ${index}: All properties:`, Object.keys(clinic));
                  
                  // Convert name to safe string if needed
                  let safeName = 'Unknown Clinic';
                  let safeInitial = 'C';
                  
                  try {
                    console.log(`DEBUG: CLINIC ${index}: Processing clinic.name for safe display`);
                    
                    if (clinic.name !== null && clinic.name !== undefined) {
                      if (typeof clinic.name === 'string') {
                        safeName = clinic.name;
                        console.log(`DEBUG: CLINIC ${index}: Name is string: "${safeName}"`);
                        
                        if (clinic.name && clinic.name.length > 0) {
                          try {
                            safeInitial = clinic.name.charAt(0).toUpperCase();
                            console.log(`DEBUG: CLINIC ${index}: Got initial: "${safeInitial}"`);
                          } catch (charError) {
                            console.error(`ERROR: CLINIC ${index}: Error in charAt:`, charError);
                            safeInitial = 'C';
                          }
                        } else {
                          console.log(`DEBUG: CLINIC ${index}: Name is empty string`);
                          safeInitial = 'E'; // E for Empty
                        }
                      } else {
                        console.log(`DEBUG: CLINIC ${index}: Name is not string, converting:`, typeof clinic.name);
                        safeName = String(clinic.name);
                        if (safeName && safeName.length > 0) {
                          try {
                            safeInitial = safeName.charAt(0).toUpperCase();
                          } catch (charError) {
                            console.error(`ERROR: CLINIC ${index}: Error in safeName charAt:`, charError);
                            safeInitial = 'S';
                          }
                        } else {
                          safeInitial = 'X'; // X for converted
                        }
                      }
                    } else {
                      console.log(`DEBUG: CLINIC ${index}: Name is null or undefined`);
                      safeName = 'Unknown Clinic';
                      safeInitial = 'U'; // U for Unknown
                    }
                  } catch (nameError) {
                    console.error(`ERROR: CLINIC ${index}: Error processing name:`, nameError);
                    console.error(`ERROR: CLINIC ${index}: clinic.name was:`, clinic.name);
                    console.error(`ERROR: CLINIC ${index}: Full clinic object:`, clinic);
                    safeName = 'Error Processing Name';
                    safeInitial = '!';
                  }
                  
                  console.log(`SUCCESS: CLINIC ${index}: Final safe values - name: "${safeName}", initial: "${safeInitial}"`);
                  console.log(`KEY: CLINIC ${index}: ID for operations: "${clinic.id}"`);

                  const usagePercent = Math.round(((clinic.reportsUsed || 0) / (clinic.reportsAllowed || 10)) * 100);

                  return (
                    <tr key={clinic.id || `clinic-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      {/* Clinic Name with Avatar */}
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-xs shadow-sm relative">
                            {clinic.avatar ? (
                              <img src={clinic.avatar} alt={safeName} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              safeInitial
                            )}
                            <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${clinic.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[100px] sm:max-w-[150px]">{safeName}</div>
                            <div className="md:hidden text-xs text-gray-500 dark:text-gray-400 truncate max-w-[100px]">{clinic.email || '-'}</div>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-4 py-2 whitespace-nowrap hidden md:table-cell">
                        <div className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[180px]">{clinic.email || '-'}</div>
                      </td>

                      {/* Contact Person */}
                      <td className="px-4 py-2 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[120px]">{clinic.contactPerson || '-'}</div>
                      </td>

                      {/* Phone */}
                      <td className="px-4 py-2 whitespace-nowrap hidden xl:table-cell">
                        <div className="text-sm text-gray-600 dark:text-gray-300">{clinic.phone || '-'}</div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          clinic.isActive
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${clinic.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <span className="hidden sm:inline">{clinic.isActive ? 'Active' : 'Inactive'}</span>
                          <span className="sm:hidden">{clinic.isActive ? 'A' : 'I'}</span>
                        </span>
                      </td>

                      {/* Verification */}
                      <td className="px-4 py-2 whitespace-nowrap text-center hidden sm:table-cell">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          clinic.isActivated
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}>
                          {clinic.isActivated ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Verified</>
                          ) : (
                            <><AlertTriangle className="w-3 h-3 mr-1" /> Trial</>
                          )}
                        </span>
                      </td>

                      {/* Subscription */}
                      <td className="px-4 py-2 whitespace-nowrap text-center hidden lg:table-cell">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                          {clinic.subscriptionStatus || 'Trial'}
                        </span>
                      </td>

                      {/* Reports Usage */}
                      <td className="px-4 py-2 whitespace-nowrap hidden xl:table-cell">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 min-w-[80px]">
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                              <div
                                className="bg-blue-500 dark:bg-blue-400 h-1.5 rounded-full transition-all"
                                style={{ width: `${usagePercent}%` }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {clinic.reportsUsed || 0}/{clinic.reportsAllowed || 10}
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-1.5">
                          <button
                            onClick={() => viewClinicDetails(clinic)}
                            className="p-1.5 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-md transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => openModal(clinic)}
                            className="p-1.5 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-md transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => handleDeactivateClinic(clinic.id)}
                            className={`p-1.5 rounded-md transition-colors ${
                              clinic.isActive
                                ? 'bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/50 text-orange-600 dark:text-orange-400'
                                : 'bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 text-green-600 dark:text-green-400'
                            }`}
                            title={clinic.isActive ? 'Deactivate Clinic' : 'Activate Clinic'}
                          >
                            {clinic.isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                          </button>

                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Delete button clicked for:', clinic.name);
                              deleteClinic(clinic, index);
                            }}
                            className="p-1.5 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-md transition-colors cursor-pointer"
                            title="Delete Clinic"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
            } catch (error) {
              console.error(`ERROR: CLINIC ${index}: Error rendering clinic row:`, error);
              console.error(`ERROR: CLINIC ${index}: Error stack:`, error.stack);
              console.error(`ERROR: CLINIC ${index}: Error message:`, error.message);
              console.error(`ERROR: CLINIC ${index}: Clinic data:`, clinic);
              console.error(`ERROR: CLINIC ${index}: Clinic JSON:`, JSON.stringify(clinic, null, 2));
              return (
                <tr key={`error-${index}`} className="bg-red-50">
                  <td colSpan="9" className="px-4 py-4 text-center">
                    <div className="text-red-700 text-sm">
                      <p>ERROR: Error displaying clinic {index + 1}</p>
                      <p className="text-xs text-red-500 font-mono">Error: {error.message}</p>
                    </div>
                  </td>
                </tr>
              );
            }
          });
            } catch (mapError) {
              console.error('ERROR: CRITICAL: Error in clinics.map:', mapError);
              console.error('ERROR: CRITICAL: Error stack:', mapError.stack);
              console.error('ERROR: CRITICAL: Clinics data:', clinics);
              return (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center">
                    <div className="bg-red-100 border border-red-300 rounded-lg p-4 inline-block">
                      <p className="text-red-700">ERROR: Critical Error: Unable to render clinics</p>
                      <p className="text-xs text-red-500 font-mono">Map Error: {mapError.message}</p>
                    </div>
                  </td>
                </tr>
              );
            }
          })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {clinics.length === 0 && (
          <div className="text-center py-20">
            <div className="relative">
              <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-16 w-16 text-slate-400" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">No Project Areas Registered Yet</h3>
              <p className="text-slate-500 text-lg mb-8">Start by adding your first project area to the platform</p>
              <button
                onClick={() => openModal()}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                <div className="relative flex items-center space-x-3">
                  <Plus className="h-6 w-6" />
                  <span>Add Your First Project Area</span>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <ClinicModal
          clinic={selectedClinic}
          onSubmit={selectedClinic ? handleEditClinic : handleCreateClinic}
          onClose={closeModal}
          register={register}
          handleSubmit={handleSubmit}
          errors={errors}
        />
      )}

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <PasswordResetModal
          clinic={selectedClinic}
          newPassword={newPassword}
          isManualPassword={isManualPassword}
          onConfirm={confirmPasswordReset}
          onClose={() => {
            setShowPasswordReset(false);
            setNewPassword('');
            setSelectedClinic(null);
            setIsManualPassword(false);
          }}
          onCopy={copyPasswordToClipboard}
          onGeneratePassword={generateNewPassword}
          onManualPassword={handleManualPassword}
          onPasswordChange={setNewPassword}
        />
      )}
    </div>
  );
};

// Clinic Modal Component  
const ClinicModal = ({ clinic, onSubmit, onClose, register, handleSubmit, errors }) => {
  const [selectedPlan, setSelectedPlan] = React.useState('trial');
  const [showPassword, setShowPassword] = React.useState(false);

  const getReportsLimitForPlan = (plan) => {
    switch (plan) {
      case 'trial': return 10;
      case 'basic': return 50;
      case 'premium': return 200;
      case 'enterprise': return 1000; // Show 1000 for unlimited UI
      default: return 10;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border max-w-md w-full shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {clinic ? 'Edit Project Area' : 'Add New Project Area'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Clinic Profile Picture Display */}
        {clinic && clinic.avatar && (
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#E4EFFF]0 via-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-lg overflow-hidden">
                <img 
                  src={clinic.avatar} 
                  alt={`${clinic.name} Profile`} 
                  className="w-20 h-20 object-cover"
                  onError={(e) => {
                    console.log(`IMAGE: Profile picture failed to load for ${clinic.name} in modal`);
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-20 h-20 flex items-center justify-center hidden">
                  <Users className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-white">
                {clinic.isActive ? (
                  <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
                ) : (
                  <div className="w-3 h-3 bg-gradient-to-r from-red-400 to-red-500 rounded-full"></div>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Area Name *
            </label>
            <input
              type="text"
              {...register('name', { required: 'Project area name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              {...register('email', { 
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Manager / Contact Person
            </label>
            <input
              type="text"
              {...register('contactPerson')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Phone
            </label>
            <input
              type="tel"
              {...register('phone')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Address / Location
            </label>
            <textarea
              {...register('address')}
              rows="3"
              placeholder="Physical location of the project area or facility"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {clinic && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type={showPassword ? "text" : "password"}
                  value={clinic.password || 'Not Set'}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const password = clinic.password || '';
                      if (navigator.clipboard && window.isSecureContext) {
                        await navigator.clipboard.writeText(password);
                        toast.success('Password copied to clipboard!');
                      } else {
                        // Fallback for non-secure context
                        const textArea = document.createElement('textarea');
                        textArea.value = password;
                        textArea.style.position = 'fixed';
                        textArea.style.left = '-999999px';
                        textArea.style.top = '-999999px';
                        document.body.appendChild(textArea);
                        textArea.focus();
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        toast.success('Password copied to clipboard!');
                      }
                    } catch (error) {
                      console.warn('Could not copy to clipboard:', error);
                      toast.error('Could not copy password');
                    }
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  title="Copy password to clipboard"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Current login password for this clinic. Use "Reset Password" button to change.
              </p>
            </div>
          )}

          {!clinic && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  {...register('confirmPassword', {
                    required: 'Password confirmation is required',
                    validate: (value, formValues) =>
                      value === formValues.password || 'Passwords do not match'
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription Plan
                </label>
                <select
                  {...register('subscriptionPlan')}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="trial">Trial (10 reports)</option>
                  <option value="basic">Basic (50 reports)</option>
                  <option value="premium">Premium (200 reports)</option>
                  <option value="enterprise">Enterprise (Unlimited)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  P&ID Reports Limit
                  {selectedPlan === 'enterprise' && (
                    <span className="text-xs text-gray-500 ml-1">(Unlimited)</span>
                  )}
                </label>
                <input
                  type="number"
                  {...register('reportsAllowed', { 
                    min: { value: 1, message: 'Minimum 1 report required' }
                  })}
                  defaultValue={getReportsLimitForPlan(selectedPlan)}
                  key={selectedPlan} // Force re-render when plan changes
                  disabled={selectedPlan === 'enterprise'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                />
                {errors.reportsAllowed && <p className="text-red-500 text-xs mt-1">{errors.reportsAllowed.message}</p>}
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 border border-transparent rounded-lg text-sm font-medium text-white hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {clinic ? 'Update Project Area' : 'Create Project Area'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Clinic Details Component
const ClinicDetails = ({ clinic, onBack }) => {
  const [supervisors, setPatients] = useState([]);
  const [reports, setReports] = useState([]);
  const [usage, setUsage] = useState({});

  useEffect(() => {
    if (clinic) {
      const clinicSupervisors = DatabaseService.getSupervisorsByClinic(clinic.id);
      const clinicReports = DatabaseService.getReportsByClinic(clinic.id);
      const clinicUsage = DatabaseService.getClinicUsage(clinic.id);
      
      setPatients(clinicSupervisors);
      setReports(clinicReports);
      setUsage(clinicUsage);
    }
  }, [clinic]);

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="text-primary-600 hover:text-primary-800 font-medium"
        >
          ← Back to Clinics
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#E4EFFF]0 via-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-lg overflow-hidden">
                {clinic.avatar ? (
                  <img 
                    src={clinic.avatar} 
                    alt={`${clinic.name} Profile`} 
                    className="h-16 w-16 object-cover"
                    onError={(e) => {
                      console.log(`IMAGE: Profile picture failed to load for ${clinic.name}, showing icon`);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`h-16 w-16 flex items-center justify-center ${clinic.avatar ? 'hidden' : ''}`}>
                  <Users className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-white">
                {clinic.isActive ? (
                  <div className="w-2.5 h-2.5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
                ) : (
                  <div className="w-2.5 h-2.5 bg-gradient-to-r from-red-400 to-red-500 rounded-full"></div>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{clinic.name}</h2>
              <p className="text-gray-600">{clinic.email}</p>
            </div>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            clinic.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {clinic.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{clinic.email}</span>
              </div>
              {clinic.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">{clinic.phone}</span>
                </div>
              )}
              {clinic.address && (
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <span className="text-sm text-gray-600">{clinic.address}</span>
                </div>
              )}
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">
                  Joined {new Date(clinic.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Usage Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Reports Used:</span>
                <span className="font-semibold">{clinic.reportsUsed || 0} / {clinic.reportsAllowed || 10}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Patients:</span>
                <span className="font-semibold">{supervisors.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Reports:</span>
                <span className="font-semibold">{reports.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Subscription:</span>
                <span className="font-semibold capitalize">{clinic.subscriptionStatus || 'Trial'}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm">
                View All Patients
              </button>
              <button className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm">
                View All Reports
              </button>
              <button className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm">
                Manage Subscription
              </button>
              <button className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm">
                Send Notification
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Password Reset Modal Component
const PasswordResetModal = ({ 
  clinic, 
  newPassword, 
  isManualPassword,
  onConfirm, 
  onClose, 
  onCopy,
  onGeneratePassword,
  onManualPassword,
  onPasswordChange
}) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border max-w-lg w-full shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Key className="h-5 w-5 text-orange-500" />
            <h3 className="text-lg font-medium text-gray-900">Set Password & Send Credentials</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-[#E4EFFF] border border-blue-200 rounded-md p-4">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-800">
                  Email Notification for {clinic?.name}
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Username, password, and activation OTP will be sent to: <strong>{clinic?.email}</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Password Input Options */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Choose Password Method:
            </label>
            
            <div className="flex space-x-3">
              <button
                onClick={onGeneratePassword}
                className={`flex-1 p-3 border rounded-md text-sm font-medium transition-colors ${
                  !isManualPassword 
                    ? 'border-primary-500 bg-primary-50 text-primary-700' 
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <RefreshCw className="h-4 w-4 mx-auto mb-1" />
                Generate Random
              </button>
              <button
                onClick={onManualPassword}
                className={`flex-1 p-3 border rounded-md text-sm font-medium transition-colors ${
                  isManualPassword 
                    ? 'border-primary-500 bg-primary-50 text-primary-700' 
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Edit className="h-4 w-4 mx-auto mb-1" />
                Manual Entry
              </button>
            </div>
          </div>

          {/* Password Input Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isManualPassword ? 'Enter Password:' : 'Generated Password:'}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={newPassword}
                onChange={(e) => onPasswordChange(e.target.value)}
                readOnly={!isManualPassword}
                placeholder={isManualPassword ? "Enter password (min 6 characters)" : "Click 'Generate Random' to create password"}
                className={`flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  !isManualPassword ? 'bg-gray-50 font-mono' : ''
                }`}
              />
              {!isManualPassword && newPassword && (
                <button
                  onClick={onCopy}
                  className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  title="Copy to clipboard"
                >
                  <Copy className="h-4 w-4" />
                </button>
              )}
            </div>
            {isManualPassword && (
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 6 characters
              </p>
            )}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-md p-3">
            <p className="text-sm text-green-700">
              <strong>What happens next:</strong>
              <br />• Username (email) and password will be sent to clinic
              <br />• 6-digit OTP will be sent for account activation
              <br />• Clinic must verify OTP to activate their account
              <br />• OTP expires in 15 minutes
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!newPassword.trim()}
            className="px-4 py-2 bg-[#323956] border border-transparent rounded-md text-sm font-medium text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Mail className="h-4 w-4" />
            <span>Send Credentials & OTP</span>
          </button>
        </div>
      </div>

      {/* Admin Assignment Modal */}
      <AdminAssignmentModal
        clinic={selectedClinicForAdmin}
        isOpen={showAdminModal}
        onClose={() => {
          setShowAdminModal(false);
          setSelectedClinicForAdmin(null);
        }}
        onUpdate={loadClinics}
      />
    </div>
  );
};

export default ClinicManagement;